import React from 'react';
import { motion } from 'framer-motion';

export function Services() {
  const items = [
    { title: 'Consulting', desc: 'Strategy, M&A, market entry, and transformation.' },
    { title: 'Investment', desc: 'Portfolio design, asset allocation, and alpha research.' },
    { title: 'Web3', desc: 'Token economics, staking strategy, and digital asset custody.' },
    { title: 'Treasury', desc: 'Yield, liquidity, and risk across market cycles.' },
  ];
  return (
    <section id="services" className="py-16 bg-white">
      <div className="max-w-7xl mx-auto px-6">
        <h2 className="text-3xl font-bold text-gray-900">Services</h2>
        <p className="mt-2 text-gray-600">Advisory across traditional and digital markets.</p>
        <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {items.map((it, i) => (
            <motion.div key={it.title} className="rounded-xl border border-primary/10 p-6 shadow-sm"
              initial={{ opacity: 0, y: 12 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.05 }}
            >
              <h3 className="text-lg font-semibold text-gray-900">{it.title}</h3>
              <p className="mt-2 text-gray-600 text-sm">{it.desc}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

export function Markets() {
  const tickers = ['META','AAPL','NFLX','GOOGL','AMZN','TSLA','MSFT','NVDA','BTC','ETH','XAU'];
  const base = 'https://img.logo.dev/ticker';
  const token = 'pk_Pb2cclXnTuuh7Lsj1Ys7ew';
  return (
    <section id="markets" className="py-8 bg-gray-50">
      <div className="max-w-7xl mx-auto px-6">
        <div className="overflow-hidden relative">
          <div className="flex gap-8 animate-[marquee_25s_linear_infinite] will-change-transform">
            {tickers.concat(tickers).map((t, i) => (
              <img key={`${t}-${i}`} src={`${base}/${t}?token=${token}&retina=true`} alt={t} className="h-10 w-10 object-contain" />
            ))}
          </div>
        </div>
      </div>
      <style>{`@keyframes marquee { 0%{transform:translateX(0)} 100%{transform:translateX(-50%)} }`}</style>
    </section>
  );
}

export function Stats() {
  // Lightweight animated counter without extra deps
  const CountUp = ({ end, duration = 2000, prefix = '', suffix = '', decimals = 0 }) => {
    const [value, setValue] = React.useState(0);
    const startTs = React.useRef(null);
    React.useEffect(() => {
      let raf;
      const start = 0;
      const animate = (ts) => {
        if (!startTs.current) startTs.current = ts;
        const progress = Math.min((ts - startTs.current) / duration, 1);
        const current = start + (end - start) * progress;
        setValue(current);
        if (progress < 1) raf = requestAnimationFrame(animate);
      };
      raf = requestAnimationFrame(animate);
      return () => cancelAnimationFrame(raf);
    }, [end, duration]);

    const formatted = React.useMemo(() => {
      const factor = Math.pow(10, decimals);
      const rounded = Math.round(value * factor) / factor;
      return `${prefix}${rounded.toLocaleString(undefined, { minimumFractionDigits: decimals, maximumFractionDigits: decimals })}${suffix}`;
    }, [value, prefix, suffix, decimals]);

    return <span>{formatted}</span>;
  };

  const stats = [
    { label: 'Assets Under Management', end: 5.2, prefix: '$', suffix: 'B+', decimals: 1 },
    { label: 'Markets Covered', end: 35, suffix: '+', decimals: 0 },
    { label: 'Clients Served', end: 500, suffix: '+', decimals: 0 },
    { label: 'Winning Strategies', end: 72, suffix: '%', decimals: 0 },
  ];

  return (
    <section id="stats" className="py-20 bg-gradient-to-b from-[#0b1020] to-[#0a0f1c]">
      <div className="max-w-7xl mx-auto px-6">
        <div data-aos="fade-up" className="text-center mb-12">
          <h2 className="text-3xl font-semibold text-white">Strength in Numbers</h2>
          <p className="mt-3 text-white/70 max-w-2xl mx-auto">
            Proven performance, deep market reach, and trusted relationships across global markets.
          </p>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-6">
          {stats.map((s, i) => (
            <div
              key={s.label}
              data-aos="fade-up"
              data-aos-delay={`${150 + i * 100}`}
              className="rounded-2xl p-6 text-center bg-white/5 border border-white/10 shadow-[0_10px_30px_rgba(0,0,0,0.35)] backdrop-blur-sm"
            >
              <div className="text-2xl sm:text-3xl font-extrabold text-amber-300">
                <CountUp end={s.end} prefix={s.prefix} suffix={s.suffix} decimals={s.decimals} />
              </div>
              <div className="mt-2 text-white/80 text-sm">{s.label}</div>
          </div>
        ))}
        </div>
      </div>
    </section>
  );
}

export function Offerings() {
  const offerings = [
    {
      key: 'wealth',
      title: 'Wealth Management',
      description: 'Personalized investment mandates with institutional-grade risk management and downside protection.',
      features: ['Portfolio Optimization', 'Risk Assessment', 'Tax Efficiency', 'Estate Planning'],
    },
    {
      key: 'institutional',
      title: 'Institutional Services',
      description: 'Bespoke strategies, execution, and comprehensive reporting for institutional clients.',
      features: ['Custom Strategies', 'Execution Services', 'Performance Reporting', 'Compliance Support'],
    },
    {
      key: 'research',
      title: 'Research & Analytics',
      description: 'Macro, sector, and chain analytics providing timely insights for informed investment decisions.',
      features: ['Market Research', 'Sector Analysis', 'Risk Modeling', 'Performance Analytics'],
    },
  ];

  const renderIcon = (key) => {
    const common = { width: 24, height: 24, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 2, strokeLinecap: 'round', strokeLinejoin: 'round', className: 'text-white' };
    switch (key) {
      case 'wealth':
        // Briefcase
        return (
          <svg {...common}>
            <path d="M4 7h16a2 2 0 012 2v8a2 2 0 01-2 2H4a2 2 0 01-2-2V9a2 2 0 012-2z" />
            <path d="M9 7V5a2 2 0 012-2h2a2 2 0 012 2v2" />
          </svg>
        );
      case 'institutional':
        // Building
        return (
          <svg {...common}>
            <path d="M3 21h18" />
            <path d="M6 21V9l6-4 6 4v12" />
            <path d="M9 21v-6h6v6" />
          </svg>
        );
      case 'research':
      default:
        // Chart
        return (
          <svg {...common}>
            <path d="M3 3v18h18" />
            <path d="M7 15l4-4 3 3 4-6" />
          </svg>
        );
    }
  };

  return (
    <section id="offerings" className="py-20 bg-gradient-to-br from-gray-50 to-blue-50">
      <div className="max-w-7xl mx-auto px-6">
        <div
          data-aos="fade-up"
          data-aos-delay="100"
          className="text-center mb-16"
        >
          <h2 className="text-4xl font-semibold text-gray-900 mb-4">Our Core Offerings</h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto text-center">
            Comprehensive wealth management solutions designed to meet your unique investment objectives and risk profile
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {offerings.map((offering, index) => (
            <div
              key={offering.title}
              data-aos="fade-up"
              data-aos-delay={`${200 + index * 150}`}
              className="bg-white rounded-2xl shadow-lg hover:shadow-xl transition-all duration-500 hover:scale-105 overflow-hidden"
            >
              <div className="p-8">
                <div className="w-16 h-16 rounded-full bg-[#0056d7] flex items-center justify-center shadow-md mb-6">
                  {renderIcon(offering.key)}
                </div>
                <h3 className="text-2xl font-semibold text-gray-900 mb-4">{offering.title}</h3>
                <p className="text-gray-600 leading-relaxed mb-6">{offering.description}</p>
                
                <ul className="space-y-3">
                  {offering.features.map((feature, featureIndex) => (
                    <li key={featureIndex} className="flex items-center gap-3">
                      <div className="w-2 h-2 bg-[#0056d7] rounded-full flex-shrink-0"></div>
                      <span className="text-gray-700">{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>
              
              <div className="px-8 pb-8">
                <motion.a
                  href={offering.key === 'wealth' ? '/wealth-management' : offering.key === 'institutional' ? '/institutional-services' : '/research-analytics'}
                  className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-gradient-to-r from-[#0056d7] to-[#003a9e] hover:from-[#003a9e] hover:to-[#002a73] text-white font-medium transition-all duration-500 shadow-lg hover:shadow-blue-500/25"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  Learn More
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M5 12h14" />
                    <path d="M12 5l7 7-7 7" />
                  </svg>
                </motion.a>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

export function Testimonials() {
  const testimonials = [
    {
      quote: "Blockfirst Capital's disciplined approach to portfolio allocation has consistently delivered superior risk-adjusted returns for our institutional clients. Their data-driven methodology and sophisticated risk management framework set them apart in the industry.",
      author: "Sarah Mitchell",
      title: "Chief Investment Officer",
      company: "Global FinTech Partners",
      avatar: "/images/people/pexels-mart-production-7222273.jpg"
    },
    {
      quote: "The market research and risk controls provided by Blockfirst Capital are best-in-class. Their team's deep understanding of global markets and innovative investment strategies have been instrumental in our portfolio's performance.",
      author: "Michael Chen",
      title: "Managing Partner",
      company: "Venture Capital Partners",
      avatar: "/images/people/pexels-pavel-danilyuk-5520284.jpg"
    },
    {
      quote: "Transparency and consistent performance are what we value most, and Blockfirst Capital delivers both. Their comprehensive reporting and client-centric approach have made them our trusted wealth management partner.",
      author: "Sophia Müller",
      title: "Principal",
      company: "Müller Family Office (DE)",
      avatar: "/images/people/pexels-yankrukov-7693223.jpg"
    },
  ];

  return (
    <section id="testimonials" className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-6">
        <div
          data-aos="fade-up"
          data-aos-delay="100"
          className="text-center mb-16"
        >
          <h2 className="text-4xl font-semibold text-gray-900 mb-4">What Our Clients Say</h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto text-center">
            Trusted by leading institutions and high-net-worth individuals worldwide
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {testimonials.map((testimonial, index) => (
            <div
              key={testimonial.author}
              data-aos="fade-up"
              data-aos-delay={`${200 + index * 150}`}
              className="bg-gradient-to-br from-gray-50 to-white rounded-2xl shadow-lg hover:shadow-xl transition-all duration-500 p-8 border border-gray-100"
            >
              <div className="flex items-center gap-1 mb-6">
                {[...Array(5)].map((_, i) => (
                  <svg key={i} width="20" height="20" viewBox="0 0 24 24" fill="currentColor" className="text-[#fbbf24]">
                    <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
                  </svg>
                ))}
              </div>
              
              <blockquote className="text-gray-700 leading-relaxed mb-6 text-lg">
                "{testimonial.quote}"
            </blockquote>
              
              <div className="flex items-center gap-4">
                <img
                  src={testimonial.avatar}
                  alt={testimonial.author}
                  className="w-12 h-12 rounded-full object-cover shadow-md"
                />
                <div>
                  <div className="font-semibold text-gray-900">{testimonial.author}</div>
                  <div className="text-sm text-[#0056d7] font-medium">{testimonial.title}</div>
                  <div className="text-sm text-gray-500">{testimonial.company}</div>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div
          data-aos="fade-up"
          data-aos-delay="600"
          className="text-center mt-12"
        >
          <div className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-gray-100 text-gray-600">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M9 12l2 2 4-4" />
              <path d="M21 12c-1 0-3-1-3-3s2-3 3-3 3 1 3 3-2 3-3 3" />
              <path d="M3 12c1 0 3-1 3-3s-2-3-3-3-3 1-3 3 2 3 3 3" />
              <path d="M3 12h6" />
              <path d="M15 12h6" />
            </svg>
            <span className="text-sm font-medium">Trusted by 500+ clients worldwide</span>
          </div>
        </div>
      </div>
    </section>
  );
}

export function CallToAction() {
  return (
    <section id="contact" className="py-20 bg-gradient-to-br from-[#0056d7] via-[#003a9e] to-[#002a73] text-white relative overflow-hidden">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute inset-0" style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.1'%3E%3Ccircle cx='30' cy='30' r='2'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
        }} />
      </div>

      <div className="max-w-7xl mx-auto px-6 relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          {/* Content */}
          <div
            data-aos="fade-right"
            data-aos-delay="100"
            className="text-center lg:text-left"
          >
            <h2 className="text-4xl lg:text-5xl font-light mb-6">
              Ready to <span className="font-semibold text-[#fbbf24]">Transform</span> Your Wealth?
            </h2>
            <p className="text-xl text-white/90 leading-relaxed mb-8">
              Join 500+ discerning investors who trust Blockfirst Capital with their financial future. 
              Schedule a confidential consultation with our wealth management experts.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start">
              <motion.a
                href="/contact"
                className="inline-flex items-center gap-3 px-8 py-4 rounded-full bg-gradient-to-r from-[#fbbf24] to-[#f59e0b] hover:from-[#f59e0b] hover:to-[#d97706] text-black font-semibold text-lg transition-all duration-500 shadow-xl hover:shadow-amber-500/30"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
                </svg>
                Schedule Consultation
              </motion.a>
              
              <motion.a
                href="mailto:info@blockfirstcapital.com"
                className="inline-flex items-center gap-3 px-8 py-4 rounded-full bg-white/10 hover:bg-white/20 text-white font-semibold text-lg transition-all duration-500 border border-white/20 hover:border-white/40"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
                  <polyline points="22,6 12,13 2,6" />
                </svg>
                Email Us
              </motion.a>
            </div>

            <div className="mt-8 flex flex-wrap justify-center lg:justify-start gap-8 text-white/80">
              <div className="flex items-center gap-2">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M9 12l2 2 4-4" />
                </svg>
                <span className="text-sm">Free Initial Consultation</span>
              </div>
              <div className="flex items-center gap-2">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
                </svg>
                <span className="text-sm">Confidential & Secure</span>
              </div>
            </div>
          </div>

          {/* Image */}
          <div
            data-aos="fade-left"
            data-aos-delay="200"
            className="relative"
          >
            <div className="relative rounded-2xl overflow-hidden shadow-2xl">
              <img
                src="/new-assets/pexels-karola-g-7876662.jpg"
                alt="Professional consultation meeting"
                className="w-full h-96 object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0056d7]/20 to-transparent" />
              
              {/* Floating Stats */}
              <div className="absolute bottom-6 left-6 right-6">
                <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 border border-white/20">
                  <div className="grid grid-cols-3 gap-4 text-center">
                    <div>
                      <div className="text-2xl font-bold text-white">$5.2B+</div>
                      <div className="text-xs text-white/80">Assets Managed</div>
                    </div>
                    <div>
                      <div className="text-2xl font-bold text-white">500+</div>
                      <div className="text-xs text-white/80">Happy Clients</div>
                    </div>
                    <div>
                      <div className="text-2xl font-bold text-white">72%</div>
                      <div className="text-xs text-white/80">Success Rate</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

export function Footer() {
  const currentYear = new Date().getFullYear();

  const footerLinks = {
    services: [
      { name: 'Portfolio Management', href: '/portfolio-management' },
      { name: 'Financial Planning', href: '/financial-planning' },
      { name: 'Risk Management', href: '/risk-management' },
      { name: 'Wealth Management', href: '/wealth-management' },
    ],
    investments: [
      { name: 'Healthcare', href: '/investments/healthcare' },
      { name: 'Digital Assets', href: '/investments/digital-assets' },
      { name: 'Agriculture', href: '/investments/agriculture' },
      { name: 'Real Estate', href: '/investments/real-estate' },
      { name: 'Technology', href: '/investments/technology' },
      { name: 'Industrial Technology', href: '/investments/industrial-technology' },
    ],
    company: [
      { name: 'About Us', href: '/about' },
      { name: 'Careers', href: '/careers' },
      { name: 'Contact', href: '/contact' },
    ],
    legal: [
      { name: 'Privacy Policy', href: '/privacy' },
      { name: 'Terms of Service', href: '/terms' },
      { name: 'Risk Disclosures', href: '/risk-disclosure' },
    ],
  };

  const socialLinks = [
    { name: 'LinkedIn', href: '#', icon: 'linkedin' },
    { name: 'Twitter', href: '#', icon: 'twitter' },
    { name: 'YouTube', href: '#', icon: 'youtube' },
  ];

  const getSocialIcon = (iconName) => {
    const icons = {
      linkedin: (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
          <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
        </svg>
      ),
      twitter: (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
          <path d="M23.953 4.57a10 10 0 01-2.825.775 4.958 4.958 0 002.163-2.723c-.951.555-2.005.959-3.127 1.184a4.92 4.92 0 00-8.384 4.482C7.69 8.095 4.067 6.13 1.64 3.162a4.822 4.822 0 00-.666 2.475c0 1.71.87 3.213 2.188 4.096a4.904 4.904 0 01-2.228-.616v.06a4.923 4.923 0 003.946 4.827 4.996 4.996 0 01-2.212.085 4.936 4.936 0 004.604 3.417 9.867 9.867 0 01-6.102 2.105c-.39 0-.779-.023-1.17-.067a13.995 13.995 0 007.557 2.209c9.053 0 13.998-7.496 13.998-13.985 0-.21 0-.42-.015-.63A9.935 9.935 0 0024 4.59z"/>
        </svg>
      ),
      youtube: (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
          <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
        </svg>
      ),
    };
    return icons[iconName] || null;
  };

  return (
    <footer className="bg-[#0b1020] text-white">
      <div className="max-w-7xl mx-auto px-6 py-16">
        {/* Main Footer Content */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-8 lg:gap-10 mb-12">
          {/* Company Info - Spans 2 columns on mobile and tablet */}
          <div className="col-span-2 md:col-span-3 lg:col-span-2">
            <img src="https://res.cloudinary.com/dqwxmksbe/image/upload/v1762423871/blockfirstcapital-white-logo_xeaz9r.png" alt="Blockfirst Capital" className="h-10 w-auto mb-6" />
            <p className="text-white/70 leading-relaxed mb-6 text-left">
              Blockfirst Capital is a leading wealth management firm providing sophisticated investment solutions to discerning clients worldwide.
            </p>
            <div className="flex gap-4">
              {socialLinks.map((social) => (
                <a
                  key={social.name}
                  href={social.href}
                  className="w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors duration-300"
                  aria-label={social.name}
                >
                  {getSocialIcon(social.icon)}
                </a>
              ))}
            </div>
          </div>

          {/* Services */}
          <div className="text-left">
            <h3 className="text-lg font-semibold mb-6">Services</h3>
            <ul className="space-y-3">
              {footerLinks.services.map((link) => (
                <li key={link.name}>
                  <a href={link.href} className="text-white/70 hover:text-white transition-colors duration-300 text-sm">
                    {link.name}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Investments */}
          <div className="text-left">
            <h3 className="text-lg font-semibold mb-6">Investments</h3>
            <ul className="space-y-3">
              {footerLinks.investments.map((link) => (
                <li key={link.name}>
                  <a href={link.href} className="text-white/70 hover:text-white transition-colors duration-300 text-sm">
                    {link.name}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Company & Legal Combined */}
          <div className="text-left">
            <h3 className="text-lg font-semibold mb-6">Company</h3>
            <ul className="space-y-3 mb-8">
              {footerLinks.company.map((link) => (
                <li key={link.name}>
                  <a href={link.href} className="text-white/70 hover:text-white transition-colors duration-300 text-sm">
                    {link.name}
                  </a>
                </li>
              ))}
            </ul>
            
            <h3 className="text-lg font-semibold mb-6">Legal</h3>
            <ul className="space-y-3">
              {footerLinks.legal.map((link) => (
                <li key={link.name}>
                  <a href={link.href} className="text-white/70 hover:text-white transition-colors duration-300 text-sm">
                    {link.name}
                  </a>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Contact Info */}
        <div className="border-t border-white/10 pt-8 mb-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8">
            <div className="text-left">
              <h4 className="text-lg font-semibold mb-4">Headquarters</h4>
              <p className="text-white/70">Bureau, 90 Fetter Lane<br />London, United Kingdom, EC4A 1EN</p>
            </div>
            <div className="text-left">
              <h4 className="text-lg font-semibold mb-4">Contact</h4>
              <p className="text-white/70">
                Email: info@blockfirstcapital.com
              </p>
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-white/10 pt-8 flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="text-white/60 text-sm">© {currentYear} Blockfirst Capital. All rights reserved. • Company No: 09852426</div>
          <div className="flex items-center gap-6 text-sm text-white/60">
            <span>SEC Registered Investment Advisor</span>
            <span>•</span>
            <span>FINRA Member</span>
            <span>•</span>
            <span>SIPC Protected</span>
            <span>•</span>
            <a href="/privacy" className="hover:text-white">Privacy</a>
            <span>•</span>
            <a href="/terms" className="hover:text-white">Terms</a>
          </div>
        </div>
      </div>
    </footer>
  );
}


